import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretRight, Plus, FileText, DotsSixVertical, Lock } from '@phosphor-icons/react';

function collectIds(nodes, out = []) {
  for (const n of nodes) { out.push(n.id); if (n.children?.length) collectIds(n.children, out); }
  return out;
}

function flatten(nodes, expanded, depth, out) {
  for (const n of nodes) {
    const hasKids = !!(n.children && n.children.length);
    out.push({ node: n, depth, hasKids });
    if (hasKids && expanded.has(n.id)) flatten(n.children, expanded, depth + 1, out);
  }
  return out;
}

export default function PageTree({ nodes, activeId, canEdit, onAddChild, onReorder, command }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(new Set());
  const [dragId, setDragId] = useState(null);
  const [dropHint, setDropHint] = useState(null); // { id, pos: 'before'|'after'|'inside' }
  const seenRef = useRef(new Set());

  const idsKey = useMemo(() => collectIds(nodes || []).join('|'), [nodes]);
  useEffect(() => {
    const ids = collectIds(nodes || []);
    setExpanded((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => { if (!seenRef.current.has(id)) next.add(id); }); // 新規ノードは展開
      return next;
    });
    ids.forEach((id) => seenRef.current.add(id));
  }, [idsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // すべて展開 / 折りたたむ（親からのコマンド）
  useEffect(() => {
    if (!command) return;
    if (command.type === 'expand') setExpanded(new Set(collectIds(nodes || [])));
    else if (command.type === 'collapse') setExpanded(new Set());
  }, [command]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const rows = useMemo(() => flatten(nodes || [], expanded, 0, []), [nodes, expanded]);

  if (!nodes || nodes.length === 0) {
    return <p className="px-2 py-4 text-xs text-zinc-400" data-testid="kb-tree-empty">まだページがありません</p>;
  }

  const onRowDragOver = (e, id) => {
    if (!canEdit || !dragId || dragId === id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const pos = y < rect.height * 0.28 ? 'before' : y > rect.height * 0.72 ? 'after' : 'inside';
    setDropHint((prev) => (prev && prev.id === id && prev.pos === pos ? prev : { id, pos }));
  };

  const onRowDrop = (e, id) => {
    if (!canEdit || !dragId) return;
    e.preventDefault();
    e.stopPropagation();
    const src = dragId;
    const hint = dropHint;
    setDragId(null); setDropHint(null);
    if (!hint || src === hint.id) return;
    onReorder?.(src, hint.id, hint.pos);
  };

  return (
    <div data-testid="kb-tree">
      {rows.map(({ node, depth, hasKids }) => {
        const active = node.id === activeId;
        const hint = dropHint && dropHint.id === node.id ? dropHint.pos : null;
        const dragging = dragId === node.id;
        return (
          <div
            key={node.id}
            data-testid={`kb-tree-node-${node.id}`}
            draggable={true}
            onDragStart={(e) => {
              // Batch 6 · iter_156 — sidebar drag-drop.
              // (a) Set an internal marker for the existing PageTree
              //     reorder handlers (they use `dragId` state) — canEdit
              //     only, since reorder mutates the tree.
              // (b) ALSO set `application/x-sensbey-article` payload so a
              //     drop into the RichEditor inserts a link at the drop
              //     position. This payload does NOT require canEdit — a
              //     read-only viewer can still drag an article title into
              //     an editor they're allowed to edit.
              try {
                e.dataTransfer.setData('application/x-sensbey-article', JSON.stringify({ id: node.id, title: node.title || '無題のページ' }));
              } catch { /* ignore */ }
              if (!canEdit) { e.dataTransfer.effectAllowed = 'copy'; return; }
              e.dataTransfer.effectAllowed = 'copyMove';
              setDragId(node.id);
            }}
            onDragEnd={() => { setDragId(null); setDropHint(null); }}
            onDragOver={(e) => onRowDragOver(e, node.id)}
            onDragLeave={() => setDropHint((prev) => (prev && prev.id === node.id ? null : prev))}
            onDrop={(e) => onRowDrop(e, node.id)}
            onClick={() => navigate(`/knowledge/p/${node.slug || node.id}`)}
            className={`group relative flex items-center gap-1 rounded-md pr-1 cursor-pointer ${dragging ? 'opacity-40' : ''} ${hint === 'inside' ? 'ring-2 ring-inset ring-teal-400 bg-teal-50/50' : active ? 'bg-teal-50 text-teal-900 font-medium' : 'text-zinc-700 hover:bg-zinc-100'}`}
            style={{ paddingLeft: `${depth * 12 + 4}px` }}
          >
            {hint === 'before' && <span className="pointer-events-none absolute left-1 right-1 top-0 h-0.5 rounded bg-teal-600" data-testid={`kb-drop-before-${node.id}`} />}
            {hint === 'after' && <span className="pointer-events-none absolute left-1 right-1 bottom-0 h-0.5 rounded bg-teal-600" data-testid={`kb-drop-after-${node.id}`} />}
            {hasKids ? (
              <button
                type="button"
                aria-label={`${node.title || '無題のページ'} の下位ページ`}
                aria-expanded={expanded.has(node.id)}
                title={expanded.has(node.id) ? '閉じる' : '開く'}
                data-testid={`kb-tree-toggle-${node.id}`}
                onClick={(e) => { e.stopPropagation(); toggle(node.id); }}
                className="relative h-8 w-8 -my-1.5 shrink-0 inline-flex items-center justify-center rounded text-zinc-400 hover:bg-zinc-200 focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none"
              >
                <CaretRight size={12} weight="bold" className={`transition-transform ${expanded.has(node.id) ? 'rotate-90' : ''}`} />
              </button>
            ) : (
              <span aria-hidden="true" className="h-8 w-8 -my-1.5 shrink-0 inline-block" />
            )}
            <span className="shrink-0 text-sm w-4 text-center">{node.icon || <FileText size={14} className="inline text-zinc-400" />}</span>
            <span className={`flex-1 min-w-0 truncate py-1.5 text-sm ${node.locked ? 'text-zinc-400' : ''}`} title={node.title || '無題のページ'}>{node.title || '無題のページ'}</span>
            {node.locked ? <Lock size={12} weight="fill" className="shrink-0 text-zinc-400" data-testid={`kb-lock-${node.id}`} /> : node.restricted ? <Lock size={12} className="shrink-0 text-zinc-300" data-testid={`kb-restricted-${node.id}`} /> : null}
            {node.status === 'draft' && <span className="shrink-0 rounded bg-amber-100 px-1 text-[9px] font-medium text-amber-700">下書き</span>}
            {canEdit && (
              <>
                <button
                  type="button"
                  title="子ページを追加"
                  data-testid={`kb-tree-addchild-${node.id}`}
                  onClick={(e) => { e.stopPropagation(); onAddChild(node.id); }}
                  aria-label={`${node.title || '無題のページ'} に子ページを追加`}
                  className="h-8 w-8 -my-1.5 shrink-0 inline-flex items-center justify-center rounded text-zinc-400 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:bg-zinc-200 focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none"
                >
                  <Plus size={13} weight="bold" />
                </button>
                <span
                  title="ドラッグして並び替え"
                  data-testid={`kb-tree-drag-${node.id}`}
                  className="h-5 w-4 shrink-0 inline-flex items-center justify-center rounded text-zinc-300 opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing"
                >
                  <DotsSixVertical size={13} weight="bold" />
                </span>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
